import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { AdminUrlBuilder } from '../../../../../shared/admin/api/admin-url-builder';
import { AbstractFormComponent } from '../../../../../shared/crud/component/form.component';
import { CustomerForm } from '../form/customer.form';
import {
    IConfirmationDialogPayload
} from '../../../../../shared/ui/component/confirmation-dialog/confirmation-dialog-payload.interface';
import { Language } from '../../../../../shared/enum/language';
import { ISelectOption } from '../../../../../shared/form/element/base-select/select-option.interface';
import { FormEvent } from '../../../../../shared/crud/form/form-event.enum';
import { Currency } from '../../../../../shared/enum/currency';
import * as moment from 'moment-timezone';

@Component({
    selector: 'admin-campaign-settings',
    templateUrl: './settings.component.html',
    styleUrls: [ './settings.component.scss' ]
})
export class AdminCampaignSettingsComponent extends AbstractFormComponent implements OnInit, AfterViewInit {

    enabling: any = { fs: false, ai: false };
    status: string = 'live';
    languages: any = Language;
    toggleAiBackgroundChannel: Subject<IConfirmationDialogPayload> = new Subject<IConfirmationDialogPayload>();
    toggleFaceSearchChannel: Subject<IConfirmationDialogPayload> = new Subject<IConfirmationDialogPayload>();
    toggleStatusChannel: Subject<IConfirmationDialogPayload> = new Subject<IConfirmationDialogPayload>();
    apiType: string = '';
    settlementCurrencyChangeAwareness: boolean = false;
    editSettlementCurrencyVatSubject = new Subject();
    @Input() row: any;

    currencies: any = Object.keys(Currency).map((currency: string) => {
        const label = Currency[currency].split(' ');
        label.pop();
        return {
            value: currency,
            label: label.join(' ')
        };
    });
    apis: ISelectOption[] = [
        {
            label: 'Siriusware',
            value: 'siriusware'
        },
        {
            label: 'Secondary Siriusware',
            value: 'secondarySiriusware'
        },
        {
            label: 'Adyen POS',
            value: 'adyenPos'
        },
        {
            label: 'Adyen E-commerce',
            value: 'adyenEcommerce'
        },
        {
            label: 'Solvimon',
            value: 'solvimon'
        },
        {
            label: 'Dropbox',
            value: 'dropbox'
        },
        {
            label: 'Video camera',
            value: 'videoCamera'
        },
        {
            label: 'TheArtisan AI',
            value: 'theartisan'
        },
        {
            label: 'Momants',
            value: 'momants'
        },
        {
            label: 'Iris background removal',
            value: 'irisRemoveBg'
        },
    ];
    irisRemoveBgVersions: ISelectOption[] = [
        { label: 'Default', value: '' },
        { label: '21012026_HR_matting_epoch_236 (current)', value: '21012026_HR_matting_epoch_236' },
        { label: '15102026_HR_matting_retraining_epoch_230 (trained from scratch)', value: '15102026_HR_matting_retraining_epoch_230' },
        { label: '15102026_HR_matting_epoch_308 (slightly retrained)', value: '15102026_HR_matting_epoch_308' },
        { label: 'latest', value: 'latest' }
    ];
    irisCampaignVersion: string = '';
    irisPhotographerVersions: { photographer: string, version: string }[] = [];
    campaignPhotographers: any[] = [];
    gophotoRegions: ISelectOption[] = [
        {
            label: 'EU1',
            value: 'eu1'
        },
        {
            label: 'EU1-v2',
            value: 'eu1v2'
        },
        {
            label: 'US1',
            value: 'us1'
        }
    ];
    photoWebsiteVersions: ISelectOption[] = [
        {
            label: 'v1',
            value: 'v1'
        },
        {
            label: 'v2',
            value: 'v2'
        }
    ];
    paymentPlatforms: ISelectOption[] = [
        {
            label: 'Adyen',
            value: 'adyen'
        },
        {
            label: 'Nayax',
            value: 'nayax'
        }
    ];
    merchants: ISelectOption[] = [];
    stores: ISelectOption[] = [];
    loadingMerchants: boolean = false;
    loadingStores: boolean = false;
    savedApiKey: string = '';
    timezones: string[] = [];
    reportingTimezone: string;

    currentArtisanStyle: number;
    newArtisanStyle: string = '';

    constructor(formBuilder: CustomerForm,
                httpClient: HttpClient,
                toastrService: ToastrService,
                router: Router,
                urlBuilder: AdminUrlBuilder,
                route: ActivatedRoute) {
        super(formBuilder, httpClient, toastrService, router, route, urlBuilder);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.timezones = moment.tz.names();

        this.apis = this.apis.filter((api => {
            if (api.value === 'theartisan') {
                return this.row.customer.customerLicense.features.indexOf('creation-station') > -1;
            }
            return true;
        }));

        if (!this.row.settlementCurrency) {
            this.row.settlementCurrency = 'eur';
        }

        if (this.row.apiSettings.adyenEcommerce && this.row.apiSettings.adyenEcommerce.apiKey) {
            this.savedApiKey = this.row.apiSettings.adyenEcommerce.apiKey;
            if (this.savedApiKey) {
                this.fetchMerchants();
            }
            if (this.row.apiSettings.adyenEcommerce.merchantAccount) {
                this.merchants = [ { label: '', value: '' }, {
                    label: this.row.apiSettings.adyenEcommerce.merchantAccount,
                    value: this.row.apiSettings.adyenEcommerce.merchantAccount
                } ];

                if (this.row.apiSettings.adyenEcommerce.store) {
                    this.stores = [ { label: '', value: '' }, {
                        label: this.row.apiSettings.adyenEcommerce.store,
                        value: this.row.apiSettings.adyenEcommerce.store
                    } ];
                }

                this.fetchStores(this.row.apiSettings.adyenEcommerce.merchantAccount);
            }
        }

        this.formMode = 'update';
        if (!this.row.apiSettings) {
            this.row.apiSettings = {};
        }

        this.apis = this.apis.filter(api => !this.row.apiSettings[api.value]);

        const irisRemoveBg = this.row.apiSettings.irisRemoveBg || {};
        this.irisCampaignVersion = irisRemoveBg.campaign || '';
        this.irisPhotographerVersions = Object.keys(irisRemoveBg)
            .filter(key => key !== 'campaign')
            .map(photographer => ({ photographer, version: irisRemoveBg[photographer] }));
        this.httpClient.get<{ rows: any[] }>('/admin/photographer', {
            params: { campaign: this.row.uuid, limit: '1000' }
        }).subscribe(response => {
            this.campaignPhotographers = response.rows;
        });

        if (!this.row.reportSettings) {
            this.row.reportSettings = {};
        }
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
    }

    fetchStores(merchantId: string) {
        if (!merchantId) {
            this.stores = [];
            return;
        }
        this.loadingStores = true;
        this.stores = [];
        this.httpClient.get<{
            stores: { id: string, reference: string } []
        }>(`/admin/adyen/management/store/${this.row.uuid}/${merchantId}`)
            .subscribe((r) => {
                this.stores = [ { label: '', value: '' }, ...r.stores.map(s => ({ label: s.reference, value: s.reference })) ];
                this.loadingStores = false;
            }, () => {
                this.loadingStores = false;
            });
    }

    fetchSolvimonCustomer() {
        this.httpClient.get<{ customerId: string }>(`/admin/solvimon-event/customer/${this.row.uuid}`)
            .subscribe((response) => {
                    this.row.apiSettings.solvimon.customerReference = this.row.uuid;
                    this.row.apiSettings.solvimon.customerId = response.customerId;
                }
            );
    }

    onAdyenApiKeyChange(value: string) {
        this.merchants = [];
        this.stores = [];
        this.loadingMerchants = false;
        this.row.apiSettings.adyenEcommerce.merchantAccount = null;
        this.row.apiSettings.adyenEcommerce.store = null;
    }

    fetchMerchants() {
        this.loadingMerchants = true;
        this.merchants = [];
        this.httpClient.get<{ merchants: string[] }>(`/admin/adyen/management/merchant/${this.row.uuid}`)
            .subscribe((r) => {
                this.merchants = [ { label: '', value: '' }, ...r.merchants.map(m => ({ label: m, value: m })) ];
                this.loadingMerchants = false;
            }, () => {
                this.loadingMerchants = false;
            });
    }

    protected getUrl(): string {
        return `/campaign/${this.row.id}`;
    }

    protected onSubmit(response: any): void {
        this.toastrService.success('Saved');
    }

    reset() {
        this.populate().subscribe();
    }

  toggleFacialRecognition(campaign: any, flag: boolean) {
    this.enabling.fs = true;
    this.httpClient.put(`/admin/campaign/${campaign.uuid}/facial-recognition`, {
      isActive: Boolean(flag)
    })
      .subscribe((response: any) => {
        this.row.facialRecognitionStatus = response.facialRecognitionStatus;
        this.toggleFaceSearchChannel.next(<IConfirmationDialogPayload>{
          message: 'Success',
          result: true
        });
      }, (error: any) => {
        this.toastrService.error('Could not enable facial recognition');
        this.toggleFaceSearchChannel.next(<IConfirmationDialogPayload>{
          message: 'Failed',
          result: false
        });
      }).add(() => {
      this.enabling.fs = false;
    });
  }

  toggleAiBackgroundRemoval(campaign: any, flag: boolean) {
    this.enabling.ai = true;
    this.httpClient.put(`/admin/campaign/${campaign.uuid}/ai-background-removal`, {
      isActive: Boolean(flag)
    })
      .subscribe((response: any) => {
        this.row.aiBackgroundRemovalStatus = response.aiBackgroundRemovalStatus;
        this.toggleAiBackgroundChannel.next(<IConfirmationDialogPayload>{
          message: 'Success',
          result: true
        });
      }, (error: any) => {
        this.toastrService.error('Could not enable AI background removal');
        this.toggleAiBackgroundChannel.next(<IConfirmationDialogPayload>{
          message: 'Failed',
          result: false
        });
      }).add(() => {
      this.enabling.ai = false;
    });
  }

  changeStatus(campaign, flag: boolean) {
    campaign.status = flag ? 'live' : 'test';
    this.httpClient.put(`/admin/campaign/${campaign.uuid}/status`, {
      isActive: Boolean(flag)
    }).subscribe((response: any) => {
      this.row.status = response.status;
      this.toggleStatusChannel.next(<IConfirmationDialogPayload>{
        message: 'Success',
        result: true
      });
    }, (error: any) => {
      this.toastrService.error('Could not change campaign status');
      this.toggleStatusChannel.next(<IConfirmationDialogPayload>{
        message: 'Failed',
        result: false
      });
    });
  }

    addApi() {
        if (!this.row.apiSettings[this.apiType]) {
            this.row.apiSettings[this.apiType] = {};
        }

        if (this.apiType === 'theartisan') {
            this.row.apiSettings['theartisan'].styles = [];
        }

        if (this.apiType === 'solvimon') {
            this.fetchSolvimonCustomer();
        }

        this.apis = this.apis.filter(api => !this.row.apiSettings[api.value]);
    }

    addIrisPhotographerVersion() {
        this.irisPhotographerVersions.push({ photographer: '', version: 'latest' });
    }

    removeIrisPhotographerVersion(index: number) {
        this.irisPhotographerVersions.splice(index, 1);
    }

    isIrisPhotographerTaken(photographer: string, current: { photographer: string }): boolean {
        return this.irisPhotographerVersions.some(item => item !== current && item.photographer === photographer);
    }

    private buildIrisRemoveBg(): { [key: string]: string } {
        const versions: { [key: string]: string } = {};
        if (this.irisCampaignVersion) {
            versions['campaign'] = this.irisCampaignVersion;
        }
        this.irisPhotographerVersions
            .filter(item => item.photographer && item.version)
            .forEach(item => versions[item.photographer] = item.version);
        return versions;
    }

    submit(): void {
        if (this.row.apiSettings.irisRemoveBg) {
            this.row.apiSettings.irisRemoveBg = this.buildIrisRemoveBg();
        }
        this.channel.next(FormEvent.SUBMIT);
        let url = this.getUrl();
        if (this.urlBuilder) {
            url = this.urlBuilder.buildUrl(url);
        }
        console.log(url);
        
        const adyen = this.row.apiSettings.adyenEcommerce;
        if (adyen && adyen.apiKey && (!adyen.merchantAccount || adyen.apiKey !== this.savedApiKey)) {
            this.loadingMerchants = true;
        }

        this.channel.next(FormEvent.SAVE);
        this.savePromise = this.httpClient.patch(url, {
            paymentPlatform: this.row.paymentPlatform,
            gophotoRegion: this.row.gophotoRegion,
            photoWebsiteVersion: this.row.photoWebsiteVersion || '',
            reportSettings: this.row.reportSettings,
            apiSettings: this.row.apiSettings,
            settlementCurrency: this.row.settlementCurrency
        })
            .subscribe((response: any) => {
                this.submitListener.next(response);
                Object.assign(this.row, response);

                if (this.row.apiSettings.adyenEcommerce) {
                    if (!this.row.apiSettings.adyenEcommerce.apiKey) {
                        this.savedApiKey = '';
                        this.row.apiSettings.adyenEcommerce.merchantAccount = null;
                        this.row.apiSettings.adyenEcommerce.store = null;
                        this.merchants = [];
                        this.stores = [];
                        this.loadingMerchants = false;
                    } else {
                        const apiKeyChanged = this.row.apiSettings.adyenEcommerce.apiKey !== this.savedApiKey;
                        this.savedApiKey = this.row.apiSettings.adyenEcommerce.apiKey;
                        if (apiKeyChanged || !this.row.apiSettings.adyenEcommerce.merchantAccount) {
                            this.row.apiSettings.adyenEcommerce.merchantAccount = null;
                            this.row.apiSettings.adyenEcommerce.store = null;
                            this.fetchMerchants();
                        }
                    }
                }

                return response;
            }, (error: any) => {
                if (error.error && error.error.code === 'G-CAMP-004') {
                    // The settings are saved; saving again retries the publish.
                    this.toastrService.warning('Saved, but the photowebsite version could not be published. Save again to retry.');
                    return error;
                }
                this.toastrService.error('Could not save changes');
                return error;
            });
    }

    editSettlementCurrency() {
        this.settlementCurrencyChangeAwareness = true;
        setTimeout(() => {
            this.editSettlementCurrencyVatSubject.next(<IConfirmationDialogPayload>{
                result: true,
                message: 'Edit',
            });
        }, 1);
    }

    trackByFn(index, item) {
        return index;
    }

    deleteSettings(key: string) {
        delete this.row.apiSettings[key];
    }

    addArtisanStyle() {
        if (!this.row.apiSettings.theartisan.styles || !Array.isArray(this.row.apiSettings.theartisan.styles)) {
            this.row.apiSettings.theartisan.styles = [];
        }
        this.row.apiSettings.theartisan.styles.push({});
        this.currentArtisanStyle = this.row.apiSettings.theartisan.styles.length - 1;
    }

    editArtisanStyle(index: any) {
        this.currentArtisanStyle = index;
    }

    deleteArtisanStyle(index: any) {
        this.currentArtisanStyle = null;
        this.row.apiSettings.theartisan.styles.splice(index, 1);
    }
}
